// Dialog: a modal over a scrim (a bottom sheet on a phone). Escape and a click on the scrim cancel
// it, unless `busy`; `initialFocus` takes focus when it opens (the safe choice: Cancel).
// ConfirmDialog asks the person to type a word (a repo's name) before the action enables.
// Ported from the Humint Assistant's components/ui/dialog.tsx, without Radix.
import { useEffect, useId, useRef, useState, type ReactNode, type RefObject } from 'react';
import { Button } from './Button';
import { cn } from './cn';
import { Input } from './Input';

export interface DialogProps {
  onClose: () => void;
  busy?: boolean;
  labelledBy: string;
  describedBy?: string;
  initialFocus?: RefObject<HTMLElement | null>;
  className?: string;
  children: ReactNode;
}

export function Dialog({ onClose, busy, labelledBy, describedBy, initialFocus, className, children }: DialogProps) {
  useEffect(() => {
    initialFocus?.current?.focus();
    // Once, on open: a re-render (typing in a ConfirmDialog) must not move the focus.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-scrim p-4 phone:[place-items:end_stretch] phone:p-0"
      onClick={(e) => e.target === e.currentTarget && !busy && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        aria-describedby={describedBy}
        className={cn(
          'max-h-[calc(100vh-32px)] w-[min(480px,100%)] overflow-auto rounded-lg border border-hairline-strong bg-surface-raised text-ink-body',
          'phone:max-h-[90vh] phone:w-full phone:rounded-b-none',
          className,
        )}
      >
        {children}
      </div>
    </div>
  );
}

export function DialogBody({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn('grid gap-3.5 px-5 pb-2 pt-5', className)}>{children}</div>;
}

export function DialogTitle({ id, children }: { id: string; children: ReactNode }) {
  return (
    <h2 id={id} className="m-0 text-[16px] font-semibold leading-[1.4] text-ink-display">
      {children}
    </h2>
  );
}

/** The actions, right-aligned; on a phone each fills its share of the row. */
export function DialogFooter({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-wrap justify-end gap-2 px-5 pb-[18px] pt-3.5 phone:[&>*]:min-h-11 phone:[&>*]:flex-auto phone:[&>*]:justify-center">
      {children}
    </div>
  );
}

export function ConfirmDialog({
  title,
  children,
  confirmText,
  action,
  danger = true,
  busy,
  onCancel,
  onConfirm,
}: {
  title: ReactNode;
  children?: ReactNode;
  /** What the person types to enable the action. */
  confirmText: string;
  action: string;
  danger?: boolean;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const id = useId();
  const cancel = useRef<HTMLButtonElement>(null);
  const [typed, setTyped] = useState('');
  const matches = typed.trim() === confirmText;
  return (
    <Dialog onClose={onCancel} busy={busy} labelledBy={`${id}-h`} describedBy={`${id}-d`} initialFocus={cancel}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (matches && !busy) onConfirm();
        }}
      >
        <DialogBody>
          <DialogTitle id={`${id}-h`}>{title}</DialogTitle>
          <div id={`${id}-d`} className="grid gap-2">
            {children}
          </div>
          <label className="grid gap-1.5 text-[12.5px]" htmlFor={`${id}-in`}>
            <span>
              Type <b className="font-mono">{confirmText}</b> to confirm
            </span>
            <Input id={`${id}-in`} value={typed} autoComplete="off" onChange={(e) => setTyped(e.target.value)} />
          </label>
        </DialogBody>
        <DialogFooter>
          <Button ref={cancel} onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" variant={danger ? 'danger' : 'primary'} disabled={!matches || busy}>
            {action}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
